import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  XCircle,
  Loader2,
  ClipboardCheck,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import SatisfactionSurvey from "@/components/SatisfactionSurvey";
import { DownloadManualButton } from "@/components/DownloadManualButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LETTERS = ["A", "B", "C", "D"];

// View stages
const VIEW_QUESTION = "question";
const VIEW_SUMMARY = "summary";
const VIEW_RESULT = "result";
const VIEW_REVIEW = "review";
const VIEW_SURVEY = "survey";

export default function ExamScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const student = location.state;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exam, setExam] = useState(null);
  const [answers, setAnswers] = useState({}); // question_id -> selected_index
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [view, setView] = useState(VIEW_QUESTION);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [surveyRequired, setSurveyRequired] = useState(false);

  useEffect(() => {
    if (!student || !student.course_id || !student.dni) {
      navigate("/student", { replace: true });
    }
  }, [student, navigate]);

  // Durante el examen, avisar si se intenta recargar o cerrar la pestaña
  // (el botón ATRÁS ya está neutralizado en toda la app por BackGuard).
  useEffect(() => {
    if (view !== VIEW_QUESTION && view !== VIEW_SUMMARY) return;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [view]);

  useEffect(() => {
    if (!student?.course_id) return;
    axios
      .get(`${API}/courses/${student.course_id}/survey-required`)
      .then((res) => setSurveyRequired(!!res.data?.required))
      .catch(() => setSurveyRequired(false));
    // axios/API/setSurveyRequired son estables. La unica dep variable es
    // student?.course_id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [student?.course_id]);

  // Start exam on mount
  const startExam = useCallback(async () => {
    setLoading(true);
    setError(null);
    setAnswers({});
    setResult(null);
    setView(VIEW_QUESTION);
    setCurrentIdx(0);
    try {
      const res = await axios.post(`${API}/exam/start`, {
        name: student.name,
        surname: student.surname,
        dni: student.dni,
        course_id: student.course_id,
      });
      setExam(res.data);
    } catch (e) {
      const detail = e.response?.data?.detail || "No se pudo iniciar el examen.";
      setError(typeof detail === "string" ? detail : "Error iniciando el examen");
    } finally {
      setLoading(false);
    }
    // axios/API/VIEW_QUESTION son estables. Solo dependemos de campos
    // primitivos de student.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [student?.name, student?.surname, student?.dni, student?.course_id]);

  useEffect(() => {
    if (!student) return;
    startExam();
  }, [student, startExam]);

  const total = exam?.questions?.length || 0;
  const answeredCount = useMemo(
    () =>
      exam ? exam.questions.filter((q) => answers[q.question_id] !== undefined).length : 0,
    [answers, exam]
  );

  const handleSelect = (qid, idx) => {
    setAnswers((prev) => ({ ...prev, [qid]: idx }));
  };

  const goPrev = () => setCurrentIdx((i) => Math.max(0, i - 1));
  const goNext = () => {
    if (currentIdx < total - 1) {
      setCurrentIdx((i) => i + 1);
    } else {
      // Last question → go to summary
      setView(VIEW_SUMMARY);
    }
  };
  const jumpTo = (idx) => {
    setCurrentIdx(idx);
    setView(VIEW_QUESTION);
  };

  const doSubmit = async () => {
    if (!exam) return;
    setSubmitting(true);
    try {
      const payload = {
        session_id: exam.session_id,
        answers: exam.questions.map((q) => ({
          question_id: q.question_id,
          selected_index:
            answers[q.question_id] === undefined ? -1 : answers[q.question_id],
        })),
      };
      const res = await axios.post(`${API}/exam/submit`, payload);
      setResult(res.data);
      setView(VIEW_RESULT);
    } catch (e) {
      const detail = e.response?.data?.detail || "Error al enviar el examen.";
      toast.error(typeof detail === "string" ? detail : "Error al enviar");
    } finally {
      setSubmitting(false);
    }
  };

  // -------- Loading / error guards --------
  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6 bg-[#FAFAFA] px-6">
        <div className="flex items-center gap-3 text-zinc-600">
          <Loader2 className="h-5 w-5 animate-spin" />
          Preparando examen…
        </div>
        <DownloadManualButton
          testid="exam-loading-download-manual"
          className="h-12 px-6"
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA] px-6">
        <div className="max-w-md w-full bg-white border border-zinc-200 rounded-lg p-8 text-center">
          <div className="mx-auto h-12 w-12 flex items-center justify-center bg-zinc-100 rounded-md mb-4">
            <XCircle className="h-5 w-5 text-zinc-700" />
          </div>
          <h2 className="heading-font text-2xl font-bold text-zinc-950">
            No se puede iniciar el examen
          </h2>
          <p className="mt-3 text-zinc-600 text-sm">{error}</p>
          <div className="mt-6 flex flex-col sm:flex-row gap-2 justify-center">
            <DownloadManualButton
              testid="exam-error-download-manual"
              className="h-11 px-5"
            />
            <Button
              onClick={() => navigate("/student")}
              className="h-11 px-5 bg-zinc-950 hover:bg-zinc-800 text-white rounded-md"
              data-testid="exam-error-back"
            >
              Volver
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // -------- SURVEY view --------
  if (view === VIEW_SURVEY && student?.course_id) {
    return (
      <SatisfactionSurvey
        courseId={student.course_id}
        student={student}
        onSubmitted={() => navigate("/")}
      />
    );
  }

  // -------- REVIEW view --------
  // Tras enviar el examen, permite al alumno revisar cada pregunta con su
  // respuesta marcada y la respuesta correcta resaltada. Pedido por cliente
  // 2026-06-08.
  if (view === VIEW_REVIEW && result) {
    return (
      <div
        className="min-h-screen bg-[#FAFAFA] flex flex-col"
        data-testid="exam-review"
      >
        <header className="border-b border-zinc-200 bg-white">
          <div className="max-w-3xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
            <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">
              Revisión · {result.course_name}
            </div>
            <button
              onClick={() => setView(VIEW_RESULT)}
              className="inline-flex items-center gap-2 text-sm font-medium text-zinc-700 hover:text-zinc-950 transition-colors"
              data-testid="exam-review-back"
            >
              <ChevronLeft className="h-4 w-4" />
              Volver al resultado
            </button>
          </div>
        </header>

        <main className="flex-1 px-6 py-8">
          <div className="max-w-3xl mx-auto space-y-4">
            <div className="bg-white border border-zinc-200 rounded-lg p-5 text-sm text-zinc-700">
              <div className="font-semibold text-zinc-900 mb-1">
                Aciertos: {result.correct} / {result.total}
              </div>
              Repasa cada pregunta. La opción <span className="text-emerald-700 font-semibold">verde</span> es la correcta.
              Tu respuesta queda marcada como <span className="font-semibold">«Tu respuesta»</span>.
            </div>
            {result.details.map((d, idx) => (
              <div
                key={d.question_id}
                className={`bg-white border rounded-lg p-5 ${
                  d.is_correct ? "border-emerald-300" : "border-red-300"
                }`}
                data-testid={`exam-review-q-${idx}`}
              >
                <div className="flex items-start gap-2 mb-3">
                  <span
                    className={`shrink-0 mt-0.5 inline-flex items-center justify-center h-6 w-6 rounded-full text-[12px] font-bold ${
                      d.is_correct
                        ? "bg-emerald-100 text-emerald-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {idx + 1}
                  </span>
                  <div className="flex-1 text-zinc-950 font-medium text-base leading-snug">
                    {d.text}
                  </div>
                  {d.is_correct ? (
                    <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-600 shrink-0" />
                  )}
                </div>
                <ul className="space-y-2">
                  {d.options.map((opt, oi) => {
                    const isCorrect = oi === d.correct_index;
                    const isSelected = oi === d.selected_index;
                    return (
                      <li
                        key={`${d.question_id}-opt-${oi}`}
                        className={`relative rounded-md border px-3 py-2 text-sm ${
                          isCorrect
                            ? "border-emerald-400 bg-emerald-50"
                            : isSelected
                            ? "border-red-400 bg-red-50"
                            : "border-zinc-200 bg-zinc-50"
                        }`}
                      >
                        <span className="font-semibold mr-2 text-zinc-500">
                          {String.fromCharCode(65 + oi)}.
                        </span>
                        <span className="text-zinc-900">{opt}</span>
                        {isCorrect && (
                          <span className="ml-2 text-[11px] uppercase tracking-wider text-emerald-700 font-semibold">
                            Correcta
                          </span>
                        )}
                        {isSelected && !isCorrect && (
                          <span className="ml-2 text-[11px] uppercase tracking-wider text-red-700 font-semibold">
                            Tu respuesta
                          </span>
                        )}
                        {isSelected && isCorrect && (
                          <span className="ml-2 text-[11px] uppercase tracking-wider text-emerald-700 font-semibold">
                            · Tu respuesta
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
            <div className="pt-4 flex justify-center">
              <Button
                onClick={() => setView(VIEW_RESULT)}
                className="h-12 px-8 bg-zinc-950 hover:bg-zinc-800 text-white rounded-md font-semibold"
                data-testid="exam-review-finish"
              >
                Volver al resultado
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // -------- RESULT view --------
  if (view === VIEW_RESULT && result) {
    const pct = total ? Math.round((result.correct / total) * 100) : 0;
    const isLastAttempt = (result.attempts_remaining ?? 0) <= 0;
    return (
      <div
        className="min-h-screen bg-[#FAFAFA] flex flex-col"
        data-testid="exam-result"
      >
        <header className="border-b border-zinc-200 bg-white">
          <div className="max-w-3xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
            <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">
              Calificación · {result.course_name}
            </div>
            <button
              onClick={() => navigate("/")}
              className="inline-flex items-center gap-2 text-sm font-medium text-zinc-700 hover:text-zinc-950 transition-colors"
              data-testid="exam-result-home"
            >
              <ChevronLeft className="h-4 w-4" />
              Inicio
            </button>
          </div>
        </header>

        <main className="flex-1 flex items-center justify-center px-6 py-10">
          <div className="max-w-xl w-full bg-white border border-zinc-200 rounded-lg p-10 md:p-14 text-center">
            <div
              className={`mx-auto h-20 w-20 flex items-center justify-center rounded-full mb-6 ${
                result.passed ? "bg-emerald-50" : "bg-red-50"
              }`}
            >
              {result.passed ? (
                <CheckCircle2 className="h-10 w-10 text-emerald-600" />
              ) : (
                <XCircle className="h-10 w-10 text-red-600" />
              )}
            </div>

            <h1
              className={`heading-font text-5xl sm:text-6xl font-extrabold leading-[0.95] ${
                result.passed ? "text-emerald-700" : "text-red-700"
              }`}
              data-testid="exam-result-status"
            >
              {result.passed ? "APTO" : "NO APTO"}
            </h1>

            <div className="mt-8" data-testid="exam-result-score">
              <div className="heading-font text-7xl sm:text-8xl font-extrabold text-zinc-950 leading-none">
                {result.correct}{" "}
                <span className="text-zinc-400">/</span> {result.total}
              </div>
              <div className="mt-2 text-zinc-500 text-sm">
                {pct}% · mínimo para aprobar {result.pass_score}%
              </div>
            </div>

            {/* If failed AND no attempts remaining, warn + INICIAR is not offered */}
            {!result.passed && isLastAttempt && (
              <div
                className="mt-8 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-left"
                data-testid="exam-last-attempt-warning"
              >
                <div className="flex items-center gap-2 text-amber-900 font-semibold">
                  <AlertTriangle className="h-4 w-4" />
                  Último intento agotado
                </div>
                <p className="text-sm text-amber-800 mt-1">
                  Has agotado los intentos disponibles para este examen.
                </p>
              </div>
            )}

            {/* If failed AND has remaining attempts, show INICIAR */}
            {!result.passed && !isLastAttempt && (
              <div
                className="mt-8 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-left"
                data-testid="exam-retry-warning"
              >
                <div className="flex items-center gap-2 text-amber-900 font-semibold">
                  <AlertTriangle className="h-4 w-4" />
                  Te queda 1 intento
                </div>
                <p className="text-sm text-amber-800 mt-1">
                  Este es tu último intento. Si no apruebas, no podrás volver a
                  intentarlo.
                </p>
              </div>
            )}

            <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
              {!result.passed && !isLastAttempt ? (
                <Button
                  onClick={startExam}
                  className="h-12 px-8 bg-zinc-950 hover:bg-zinc-800 text-white rounded-md font-semibold uppercase tracking-wide"
                  data-testid="exam-restart-button"
                >
                  Iniciar
                </Button>
              ) : (
                <Button
                  onClick={() => {
                    if (surveyRequired) {
                      setView(VIEW_SURVEY);
                    } else {
                      navigate("/");
                    }
                  }}
                  className="h-12 px-8 bg-zinc-950 hover:bg-zinc-800 text-white rounded-md font-semibold"
                  data-testid="exam-result-finish"
                >
                  {surveyRequired ? "Rellenar encuesta" : "Finalizar"}
                </Button>
              )}
              {/* Revisar examen: disponible siempre tras enviar (aprobado o no).
                  Útil sobre todo para los aprobados, que el cliente nos pidió
                  poder ver dónde fallaron y la respuesta correcta. */}
              {Array.isArray(result.details) && result.details.length > 0 && (
                <Button
                  onClick={() => setView(VIEW_REVIEW)}
                  variant="outline"
                  className="h-12 px-6 border-2 border-zinc-900 text-zinc-900 hover:bg-zinc-100 rounded-md font-semibold"
                  data-testid="exam-result-review"
                >
                  Revisar examen
                </Button>
              )}
            </div>
          </div>
        </main>
      </div>
    );
  }

  // -------- SUMMARY view --------
  if (view === VIEW_SUMMARY) {
    return (
      <div
        className="min-h-screen bg-[#FAFAFA]"
        data-testid="exam-summary"
      >
        <header className="border-b border-zinc-200 bg-white sticky top-0 z-20">
          <div className="max-w-3xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
            <button
              onClick={() => {
                setView(VIEW_QUESTION);
                setCurrentIdx(total - 1);
              }}
              className="inline-flex items-center gap-2 text-sm font-medium text-zinc-700 hover:text-zinc-950 transition-colors"
              data-testid="summary-back-button"
            >
              <ChevronLeft className="h-4 w-4" />
              Volver al examen
            </button>
            <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">
              Resumen
            </div>
          </div>
        </header>

        <main className="max-w-3xl mx-auto px-6 md:px-10 py-8 md:py-12">
          <div className="mb-8">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-zinc-500 mb-3">
              <ClipboardCheck className="h-3.5 w-3.5" />
              Resumen
            </div>
            <h1 className="heading-font text-3xl sm:text-4xl font-extrabold text-zinc-950 leading-[0.95]">
              Revisa tus respuestas
            </h1>
            <p className="mt-2 text-zinc-600 text-sm">
              Has contestado{" "}
              <strong className="text-zinc-900">{answeredCount}</strong> de{" "}
              <strong className="text-zinc-900">{total}</strong>. Pulsa sobre
              cualquier pregunta para revisarla.
            </p>
          </div>

          {/* Legend */}
          <div className="mb-4 flex items-center gap-4 text-xs">
            <span className="inline-flex items-center gap-1.5">
              <span className="h-3 w-3 rounded-sm bg-emerald-500" />
              Contestada
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-3 w-3 rounded-sm bg-red-500" />
              Sin contestar
            </span>
          </div>

          {/* Grid of question chips */}
          <div
            className="grid grid-cols-5 sm:grid-cols-8 md:grid-cols-10 gap-2"
            data-testid="summary-grid"
          >
            {exam.questions.map((q, idx) => {
              const answered = answers[q.question_id] !== undefined;
              return (
                <button
                  key={q.question_id}
                  type="button"
                  onClick={() => jumpTo(idx)}
                  data-testid={`summary-cell-${idx}`}
                  data-answered={answered ? "yes" : "no"}
                  className={`aspect-square rounded-md font-semibold text-sm transition-transform hover:scale-[1.05] focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-zinc-900 ${
                    answered
                      ? "bg-emerald-500 text-white hover:bg-emerald-600"
                      : "bg-red-500 text-white hover:bg-red-600"
                  }`}
                >
                  {idx + 1}
                </button>
              );
            })}
          </div>

          {/* Pending list */}
          {answeredCount < total && (
            <div
              className="mt-6 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-900"
              data-testid="summary-pending-warning"
            >
              Tienes <strong>{total - answeredCount}</strong>{" "}
              {total - answeredCount === 1 ? "pregunta" : "preguntas"} sin
              contestar. Si entregas el examen sin contestarlas, contarán como
              incorrectas.
            </div>
          )}

          {/* Sticky bottom bar with ENTREGAR */}
          <div className="mt-10 sticky bottom-4">
            <div className="bg-white border border-zinc-200 rounded-lg p-4 flex items-center justify-between gap-4 shadow-[0_8px_24px_-12px_rgba(0,0,0,0.15)]">
              <div className="text-sm text-zinc-700">
                <span className="font-semibold text-zinc-950">
                  {answeredCount}/{total}
                </span>{" "}
                contestadas
              </div>
              <Button
                onClick={doSubmit}
                disabled={submitting}
                className="h-12 px-8 bg-zinc-950 hover:bg-zinc-800 text-white rounded-md font-semibold uppercase tracking-wide"
                data-testid="exam-deliver-button"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Enviando…
                  </>
                ) : (
                  "Entregar"
                )}
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // -------- QUESTION view (page-by-page) --------
  const q = exam.questions[currentIdx];
  const sel = answers[q.question_id];
  const isLast = currentIdx === total - 1;

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="exam-screen">
      <header className="border-b border-zinc-200 bg-white sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between gap-3">
          <button
            onClick={() => navigate("/student")}
            className="inline-flex items-center gap-2 text-sm font-medium text-zinc-700 hover:text-zinc-950 transition-colors"
            data-testid="exam-back-button"
          >
            <ChevronLeft className="h-4 w-4" />
            Cancelar
          </button>
          <div className="text-xs uppercase tracking-[0.18em] text-zinc-500 truncate max-w-[60%] text-right">
            {exam?.course_name}
          </div>
        </div>
        {/* Progress bar */}
        <div className="h-1 w-full bg-zinc-100">
          <div
            className="h-1 bg-zinc-950 transition-all"
            style={{ width: `${((currentIdx + 1) / total) * 100}%` }}
            data-testid="exam-progress"
          />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 md:px-10 py-8 md:py-12">
        <div className="mb-6 flex items-center justify-between">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-zinc-500">
            <ClipboardCheck className="h-3.5 w-3.5" />
            Pregunta {currentIdx + 1} de {total}
          </div>
          <div className="text-xs text-zinc-500">
            {answeredCount}/{total} contestadas
          </div>
        </div>

        <div
          className="bg-white border border-zinc-200 rounded-lg p-6 md:p-8"
          data-testid={`exam-question-${currentIdx}`}
        >
          <h1 className="heading-font text-2xl sm:text-3xl font-bold text-zinc-950 leading-snug">
            {q.text}
          </h1>

          <RadioGroup
            value={sel === undefined ? "" : String(sel)}
            onValueChange={(v) => handleSelect(q.question_id, parseInt(v, 10))}
            className="mt-6 space-y-3"
          >
            {q.options.map((opt, oi) => {
              const checked = sel === oi;
              return (
                <label
                  key={`${q.question_id}-opt-${oi}`}
                  htmlFor={`${q.question_id}-${oi}`}
                  className={`flex items-start gap-3 rounded-md border-2 p-4 cursor-pointer transition-colors ${
                    checked
                      ? "border-zinc-950 bg-zinc-50"
                      : "border-zinc-200 hover:bg-zinc-50"
                  }`}
                  data-testid={`exam-option-${currentIdx}-${oi}`}
                >
                  <RadioGroupItem
                    value={String(oi)}
                    id={`${q.question_id}-${oi}`}
                    className="mt-0.5 h-5 w-5 border-2 border-zinc-300 text-zinc-950"
                  />
                  <span className="text-sm font-bold w-6 text-zinc-600">
                    {LETTERS[oi]}
                  </span>
                  <span className="text-zinc-800 leading-snug">{opt}</span>
                </label>
              );
            })}
          </RadioGroup>
        </div>

        {/* Navigation buttons */}
        <div className="mt-8 flex items-center justify-between gap-3">
          <Button
            onClick={goPrev}
            disabled={currentIdx === 0}
            variant="outline"
            className="h-12 px-5 border-2 border-zinc-300 hover:bg-zinc-50 disabled:opacity-40"
            data-testid="exam-prev-button"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            Anterior
          </Button>
          <Button
            onClick={goNext}
            className="h-12 px-8 bg-zinc-950 hover:bg-zinc-800 text-white rounded-md font-semibold uppercase tracking-wide"
            data-testid={isLast ? "exam-finalize-button" : "exam-next-button"}
          >
            {isLast ? "Finalizar" : "Siguiente"}
            {!isLast && <ChevronRight className="h-4 w-4 ml-1" />}
          </Button>
        </div>
      </main>
    </div>
  );
}
